import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  UserPlus,
  Users,
  Shield,
  Eye,
  Edit,
  Trash2,
  Loader2,
  Mail,
  Check,
  X,
  AlertCircle,
  Crown,
  Briefcase,
  Building2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const TeamPage = () => {
  const { user, token } = useAuth();
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: "", role: "viewer", member_type: "staff", client_name: "", assigned_assets: []
  });
  const [inviting, setInviting] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  const isOwner = user?.account_type !== "individual";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const promises = [
        axios.get(`${API}/team/invitations`, { headers })
      ];
      if (isOwner) {
        promises.unshift(axios.get(`${API}/team/members`, { headers }));
        promises.push(axios.get(`${API}/assets`, { headers }));
      }
      const results = await Promise.all(promises);
      if (isOwner) {
        setMembers(results[0].data);
        setInvitations(results[1].data);
        setAssets(results[2].data.filter(a => a.user_id === user?.user_id));
      } else {
        setInvitations(results[0].data);
      }
    } catch (error) {
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  }, [token, isOwner, user?.user_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInvite = async () => {
    if (!inviteForm.email) { toast.error("Please enter an email"); return; }
    if (inviteForm.member_type === "client" && !inviteForm.client_name) {
      toast.error("Please enter a client/account name"); return;
    }
    if (inviteForm.member_type === "client" && inviteForm.assigned_assets.length === 0) {
      toast.error("Please select at least one property for the client"); return;
    }
    setInviting(true);
    try {
      const payload = {
        email: inviteForm.email,
        role: inviteForm.member_type === "client" ? "viewer" : inviteForm.role,
        member_type: inviteForm.member_type,
        client_name: inviteForm.member_type === "client" ? inviteForm.client_name : null,
        assigned_assets: inviteForm.member_type === "client" ? inviteForm.assigned_assets : null
      };
      await axios.post(`${API}/team/invite`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invitation sent to ${inviteForm.email}`);
      setShowInviteModal(false);
      setInviteForm({ email: "", role: "viewer", member_type: "staff", client_name: "", assigned_assets: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      await axios.put(`${API}/team/members/${memberId}`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Role updated");
      setEditingMember(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await axios.delete(`${API}/team/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Team member removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove member");
    }
  };

  const handleAcceptInvitation = async (memberId) => {
    try {
      await axios.post(`${API}/team/invitations/${memberId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invitation accepted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to accept invitation");
    }
  };

  const handleDeclineInvitation = async (memberId) => {
    try {
      await axios.post(`${API}/team/invitations/${memberId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invitation declined");
      fetchData();
    } catch (error) {
      toast.error("Failed to decline invitation");
    }
  };

  const toggleAssetSelection = (assetId) => {
    setInviteForm(prev => ({
      ...prev,
      assigned_assets: prev.assigned_assets.includes(assetId)
        ? prev.assigned_assets.filter(id => id !== assetId)
        : [...prev.assigned_assets, assetId]
    }));
  };

  const getMemberTypeIcon = (type) => {
    return type === "client" ? Building2 : Briefcase;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  const staffMembers = members.filter(m => m.member_type !== "client");
  const clientMembers = members.filter(m => m.member_type === "client");

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Team</h1>
            <p className="text-slate-400 mt-1">Manage your staff and client access</p>
          </div>
          {isOwner && (
            <Button className="btn-primary" onClick={() => setShowInviteModal(true)} data-testid="invite-member-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          )}
        </div>

        {/* Pending Invitations (for non-owners) */}
        {invitations.length > 0 && (
          <Card className="card-dashboard border-primary/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Pending Invitations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {invitations.map((inv) => (
                <div key={inv.member_id} className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700" data-testid={`invitation-${inv.member_id}`}>
                  <div>
                    <p className="text-white font-medium">
                      {inv.owner_name || inv.owner_email} invited you
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className="capitalize bg-primary/10 text-primary border-primary/30">{inv.role}</Badge>
                      {inv.member_type === "client" && (
                        <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/30">
                          <Building2 className="w-3 h-3 mr-1" />Client: {inv.client_name}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleAcceptInvitation(inv.member_id)} data-testid={`accept-inv-${inv.member_id}`}>
                      <Check className="w-4 h-4 mr-1" /> Accept
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDeclineInvitation(inv.member_id)} data-testid={`decline-inv-${inv.member_id}`}>
                      <X className="w-4 h-4 mr-1" /> Decline
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Staff Members */}
        {isOwner && (
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-primary" />
                Staff Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              {staffMembers.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No staff members yet</p>
                  <p className="text-slate-500 text-sm mt-1">Invite staff to help manage your properties</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {staffMembers.map((member) => (
                    <div key={member.member_id} className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border border-slate-700" data-testid={`staff-member-${member.member_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          {member.role === "manager" ? <Shield className="w-5 h-5 text-primary" /> : <Eye className="w-5 h-5 text-slate-400" />}
                        </div>
                        <div>
                          <p className="text-white font-medium">{member.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`capitalize ${member.role === "manager" ? "bg-primary/10 text-primary border-primary/30" : "bg-slate-700 text-slate-300 border-slate-600"}`}>
                              {member.role}
                            </Badge>
                            <Badge className={member.status === "active" ? "status-complete" : "status-pending"}>
                              {member.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {editingMember === member.member_id ? (
                          <div className="flex gap-2">
                            <Button size="sm" variant="ghost" className="text-primary" onClick={() => handleUpdateRole(member.member_id, member.role === "manager" ? "viewer" : "manager")}>
                              Switch to {member.role === "manager" ? "Viewer" : "Manager"}
                            </Button>
                            <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => setEditingMember(null)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => setEditingMember(member.member_id)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleRemoveMember(member.member_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Client / Landlord Members */}
        {isOwner && (
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-violet-400" />
                Clients / Landlords
              </CardTitle>
            </CardHeader>
            <CardContent>
              {clientMembers.length === 0 ? (
                <div className="text-center py-8">
                  <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No clients yet</p>
                  <p className="text-slate-500 text-sm mt-1">Invite landlords or clients to view their assigned properties</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {clientMembers.map((member) => (
                    <div key={member.member_id} className="p-4 rounded-xl bg-slate-800/50 border border-slate-700" data-testid={`client-member-${member.member_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-violet-500/10 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-violet-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{member.client_name || member.email}</p>
                            <p className="text-slate-400 text-sm">{member.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className="bg-violet-500/10 text-violet-400 border-violet-500/30">Client</Badge>
                              <Badge className={member.status === "active" ? "status-complete" : "status-pending"}>
                                {member.status}
                              </Badge>
                            </div>
                          </div>
                        </div>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleRemoveMember(member.member_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      {member.assigned_assets && member.assigned_assets.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-700">
                          <p className="text-slate-500 text-xs mb-2">Assigned Properties ({member.assigned_assets.length})</p>
                          <div className="flex flex-wrap gap-2">
                            {member.assigned_assets.map((assetId) => {
                              const asset = assets.find(a => a.asset_id === assetId);
                              return (
                                <Badge key={assetId} className="bg-slate-700 text-slate-300 border-slate-600">
                                  {asset ? asset.name : assetId.slice(0, 12)}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Not owner — show info */}
        {!isOwner && invitations.length === 0 && (
          <Card className="card-dashboard">
            <CardContent className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-white text-lg font-medium mb-2">Team Management</h3>
              <p className="text-slate-400">
                Upgrade to a Corporate or Estate account to invite team members and manage client access.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Invite Modal */}
        <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
          <DialogContent className="bg-slate-900 border-slate-800 max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Invite Team Member</DialogTitle>
              <DialogDescription className="text-slate-400">Add staff or clients to your team</DialogDescription>
            </DialogHeader>
            <div className="space-y-5">
              {/* Member Type */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm font-medium">Member Type</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div
                    onClick={() => setInviteForm({ ...inviteForm, member_type: "staff", client_name: "", assigned_assets: [] })}
                    className={`p-4 rounded-xl border cursor-pointer text-center transition-all ${inviteForm.member_type === 'staff' ? 'border-primary bg-primary/5' : 'border-slate-700 hover:border-slate-600'}`}
                    data-testid="type-staff"
                  >
                    <Briefcase className={`w-8 h-8 mx-auto mb-2 ${inviteForm.member_type === 'staff' ? 'text-primary' : 'text-slate-400'}`} />
                    <p className={`font-medium ${inviteForm.member_type === 'staff' ? 'text-primary' : 'text-white'}`}>Staff</p>
                    <p className="text-slate-500 text-xs mt-1">Manage or view all properties</p>
                  </div>
                  <div
                    onClick={() => setInviteForm({ ...inviteForm, member_type: "client", role: "viewer" })}
                    className={`p-4 rounded-xl border cursor-pointer text-center transition-all ${inviteForm.member_type === 'client' ? 'border-violet-500 bg-violet-500/5' : 'border-slate-700 hover:border-slate-600'}`}
                    data-testid="type-client"
                  >
                    <Building2 className={`w-8 h-8 mx-auto mb-2 ${inviteForm.member_type === 'client' ? 'text-violet-400' : 'text-slate-400'}`} />
                    <p className={`font-medium ${inviteForm.member_type === 'client' ? 'text-violet-400' : 'text-white'}`}>Client / Landlord</p>
                    <p className="text-slate-500 text-xs mt-1">View assigned properties only</p>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className="text-slate-300">Email Address</Label>
                <Input
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="input-default"
                  data-testid="invite-email-input"
                />
              </div>

              {/* Staff Role */}
              {inviteForm.member_type === "staff" && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Role</Label>
                  <Select value={inviteForm.role} onValueChange={(val) => setInviteForm({ ...inviteForm, role: val })}>
                    <SelectTrigger className="input-default" data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manager">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-primary" />
                          <span>Manager — Can edit assets & upload documents</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4 text-slate-400" />
                          <span>Viewer — Read-only access</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Client Name */}
              {inviteForm.member_type === "client" && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Client / Account Name</Label>
                  <Input
                    placeholder="e.g., Mr. Adeyemi Properties"
                    value={inviteForm.client_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, client_name: e.target.value })}
                    className="input-default"
                    data-testid="client-name-input"
                  />
                  <p className="text-slate-500 text-xs">This name groups the client's properties for easy identification</p>
                </div>
              )}

              {/* Assign Properties (Client only) */}
              {inviteForm.member_type === "client" && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Assign Properties</Label>
                  <p className="text-slate-500 text-xs mb-2">Select which properties this client can view</p>
                  {assets.length === 0 ? (
                    <p className="text-slate-400 text-sm p-4 border border-slate-700 rounded-xl text-center">
                      No properties to assign. Add assets first.
                    </p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto p-1">
                      {assets.map((asset) => {
                        const isSelected = inviteForm.assigned_assets.includes(asset.asset_id);
                        return (
                          <div
                            key={asset.asset_id}
                            onClick={() => toggleAssetSelection(asset.asset_id)}
                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isSelected ? 'border-violet-500 bg-violet-500/5' : 'border-slate-700 hover:border-slate-600'}`}
                            data-testid={`assign-asset-${asset.asset_id}`}
                          >
                            <Checkbox checked={isSelected} className="border-slate-600" />
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium truncate ${isSelected ? 'text-violet-300' : 'text-white'}`}>{asset.name}</p>
                              <p className="text-slate-500 text-xs">{asset.state} — {asset.asset_type}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {inviteForm.assigned_assets.length > 0 && (
                    <p className="text-violet-400 text-xs">{inviteForm.assigned_assets.length} property(ies) selected</p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInviteModal(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleInvite} className="btn-primary" disabled={inviting} data-testid="send-invite-btn">
                {inviting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><UserPlus className="w-4 h-4 mr-2" />Send Invitation</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TeamPage;
